import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import our comprehensive translations
import enTranslations from '@/i18n/locales/en.json';
import ruTranslations from '@/i18n/locales/ru.json';

// Translation resources
const resources = {
  en: {
    translation: enTranslations
  },
  ru: {
    translation: ruTranslations
  }
};

/*
 * Seed the market default before the detector runs.
 *
 * Order matters. The detector reads localStorage first and falls through to
 * the browser's own language, so writing this default *after* init is too
 * late — a first-time visitor on an English browser would already have been
 * given English while localStorage claimed 'ru'.
 *
 * Written only when nothing is stored, so it is a default rather than a
 * policy. Previously i18n was pinned with `lng: 'ru'` and re-pinned by a
 * changeLanguage('ru') on every load, which made the language switcher work
 * until the next navigation and left the entire en locale unreachable.
 */
if (typeof window !== 'undefined' && !localStorage.getItem('nova-cafe-language')) {
  localStorage.setItem('nova-cafe-language', 'ru');
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    // Russian is the default for the Kyrgyzstan market, but only as a
    // fallback. `lng: 'ru'` used to be set here as well, which overrides the
    // detector outright — together with the block below it made the language
    // switcher a control that worked until the next navigation.
    fallbackLng: 'ru',
    debug: false, // Disable debug in production
    
    detection: {
      order: ['localStorage', 'querystring', 'navigator', 'htmlTag'],
      lookupLocalStorage: 'nova-cafe-language',
      caches: ['localStorage'],
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // Ensure nested keys work properly
    keySeparator: '.', // Use dot notation for nested keys
    nsSeparator: false, // Disable namespace separator
    
    // Wait for translation to be ready
    react: {
      useSuspense: false, // Don't use suspense
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '', // Return empty string for missing translations
    }
  });

// Kyrgyzstan-specific currency formatting function
export const formatKyrgyzCurrency = (amount: number): string => {
  return new Intl.NumberFormat('ru-KG', {
    style: 'currency',
    currency: 'KGS',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

// Additional Kyrgyzstan localization helpers
export const formatKyrgyzDate = (date: Date): string => {
  return new Intl.DateTimeFormat('ru-KG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'Asia/Bishkek'
  }).format(date);
};

export const formatKyrgyzTime = (date: Date): string => {
  return new Intl.DateTimeFormat('ru-KG', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Bishkek'
  }).format(date);
};

export default i18n;